import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, ProtectedRoute } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/components/AppShell";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Sales from "@/pages/Sales";
import Students from "@/pages/Students";
import Catalogue from "@/pages/Catalogue";
import Proformas from "@/pages/Proformas";
import Invoices from "@/pages/Invoices";
import Receipts from "@/pages/Receipts";
import Agency from "@/pages/Agency";
import Tax from "@/pages/Tax";
import Revenue from "@/pages/Revenue";
import EliteBase from "@/pages/EliteBase";
import Settings, { SettingsSection } from "@/pages/Settings";
import { GatewaySettings } from "@/components/GatewaySettings";
import { PageBody } from "@/components/PageParts";
import AuditLog from "@/pages/AuditLog";
import Reports from "@/pages/Reports";
import Transactions from "@/pages/Transactions";
import FlaggedItems from "@/pages/FlaggedItems";
import WebhookLogs from "@/pages/WebhookLogs";
import EmailSettings from "@/pages/EmailSettings";
import QaCleanup from "@/pages/QaCleanup";
import AdminCleanup from "@/pages/AdminCleanup";
import NumberSeriesSettings from "@/pages/NumberSeriesSettings";
import BrandingSettings from "@/pages/BrandingSettings";
import ActionRequired from "@/pages/ActionRequired";
import ActionWizard from "@/pages/ActionWizard";
import Automation from "@/pages/Automation";
import RolesPermissions from "@/pages/RolesPermissions";
import UsersManagement from "@/pages/UsersManagement";
import LearnerHistoryEmbed from "@/pages/LearnerHistoryEmbed";

const SettingsRedirect = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: to, search, hash }} replace />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/embed/learner-history" element={<LearnerHistoryEmbed />} />
          <Route element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="sales" element={<Sales />} />
            <Route path="students" element={<Students />} />
            <Route path="customers" element={<Students />} />
            <Route path="catalogue" element={<Catalogue />} />
            <Route path="proformas" element={<Proformas />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="receipts" element={<Receipts />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="action-required" element={<Automation />} />
            <Route path="automation" element={<Automation />} />
            <Route path="action-required/:tid" element={<ActionWizard />} />
            <Route path="automation/runs/:tid" element={<ActionWizard />} />
            <Route path="transactions/unmatched" element={<Transactions unmatchedOnly />} />
            <Route path="flagged" element={<FlaggedItems />} />
            <Route path="webhook-logs" element={<SettingsRedirect to="/settings/payment-webhooks" />} />
            <Route path="agency" element={<SettingsRedirect to="/settings/agency" />} />
            <Route path="tax" element={<SettingsRedirect to="/settings/tax" />} />
            <Route path="revenue" element={<Revenue />} />
            <Route path="elitebase" element={<SettingsRedirect to="/settings/elitebase" />} />
            <Route path="settings">
              <Route index element={<Settings />} />
              <Route element={<SettingsSection />}>
                <Route path="tax" element={<Tax />} />
                <Route path="agency" element={<Agency />} />
                <Route path="payment-webhooks" element={<WebhookLogs />} />
                <Route path="payment-webhooks/urls" element={<PageBody><GatewaySettings /></PageBody>} />
                <Route path="elitebase" element={<EliteBase />} />
                <Route path="audit" element={<AuditLog />} />
                <Route path="email" element={<EmailSettings />} />
                <Route path="qa-cleanup" element={<QaCleanup />} />
                <Route path="admin-cleanup" element={<AdminCleanup />} />
                <Route path="number-series" element={<NumberSeriesSettings />} />
                <Route path="branding" element={<BrandingSettings />} />
                <Route path="permissions" element={<RolesPermissions />} />
                <Route path="users" element={<UsersManagement />} />
              </Route>
            </Route>
            <Route path="audit" element={<SettingsRedirect to="/settings/audit" />} />
            <Route path="reports" element={<Reports />} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
